import type { IStyleParam, IStyleParamValuesByType, StyleParamType, createStylesParams } from '@wix/tpa-settings';
import type { IDisplayStylesParams } from './Settings/Tabs/DisplayTab/DisplayStylesParams';
import { DisplayStylesParams } from './Settings/Tabs/DisplayTab/DisplayStylesParams';
import type { IDesignStylesParams } from './Settings/Tabs/DesignTab/DesignStylesParams';
import { DesignStylesParams } from './Settings/Tabs/DesignTab/DesignStylesParams';
import type { ILayoutStylesParams } from './Settings/Tabs/LayoutTab/LayoutStylesParams';
import { LayoutStylesParams } from './Settings/Tabs/LayoutTab/LayoutStylesParams';
import type { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import {
  getColumnsImageWidth,
  getItemTextAlignment,
  getMenuMargin,
  getMenuMaxWidth,
  getMenuTextAlignment,
  getNavigationMargin,
  getSectionsColumns,
  getSectionsColumnsGap,
  getSectionsDisplay,
  getSectionsGridTemplateColumns,
  getSectionsMarginTop,
  getSectionsRowGap,
  getSectionTextAlignment,
  getSectionImageHeight,
  getSectionGridTemplate,
  getWidgetGridTemplate,
  showMenuSideImage,
  showPriceVariantsDivider,
  getElementDisplay,
  getSectionPadding,
  getSectionMarginBottom,
  getFirstSectionMarginTop,
  getSectionTopDividerMarginBottom,
  getItemsDisplay,
  getItemsGridTemplateColumns,
  getItemsRowGap,
  getItemsColumnGap,
  getItemRootMargin,
  getItemGridTemplate,
  getItemGridTemplateNoImage,
  getItemPadding,
  getItemPriceJustifySelf,
  getItemPriceMarginBottom,
  getItemImageAspectRatio,
  getItemImageMarginBottom,
  getVariantDisplay,
  getVariantGridTemplate,
  getVariantAlignItems,
  getVariantWidth,
  getVariantDetailsJustifyContent,
  getVariantDetailsGridArea,
  getWidgetPadding,
  getWidgetOverflow,
  getWidgetDisplay,
  getMenuContentGridArea,
  getMenuContentPadding,
  getImageContainerGridArea,
  getImageContainerPosition,
  getImageContainerTop,
  getImageContainerWidth,
  getImageContainerHeight,
  getImageContainerPaddingBottom,
  getSectionBottomDividerMarginTop,
  getHeaderFlexDirection,
  getHeaderContainerGridArea,
  getHeaderContainerPadding,
  getItemPriceWidth,
  convertAlignmentToJustifyContent,
  getDotSvgUrl,
  getHeaderJustifyContent,
  getHeaderAlignItems,
  getFirstItemDividerMarginBottom,
  getNameAndPriceAlignSelf,
  getDotsMargin,
  getTitleContainerDisplay,
  getTitleContainerJustifyContent,
  getTitleContainerFlexDirection,
  getSectionTitleFlexBasis,
  getTitleContainerPointerEvents,
  getBottomDividerBetweenSectionsMargin,
  getModifierContainerGridTemplate,
  getExtrasDisplay,
  getExtrasJustifyContent,
  getExtrasAlignItems,
  getExtrasFlexWrap,
  getExtrasGap,
  getSectionImageMarginBottom,
  getSectionImagePaddingBottomExpand,
  getShowSectionImageCollapse,
  shouldShowItemImage,
  shouldShowDotsBorder,
  getItemGridDividerPosition,
  getSectionColumnDividerColor,
  getSectionColumnDividerWidth,
  getSectionColumnDividerStyle,
  getDesignedDividerStyles,
  getUnifiedNavigationBackgroundPadding,
  getUnifiedNavigationGridTemplate,
  getUnifiedNavigationGridTemplateColumns,
  getLocationNavigationGridArea,
  getSectionTabsGridTemplateColumns,
  getUnifiedNavigationTextAlignment,
  getLocationsWithSectionNavigationPadding,
} from './stylesParamsUtils';
import {
  hasItemDivider,
  hasSectionBottomDivider,
  hasSectionTopDivider,
  hasTopDividerBetweenSections,
  hasBottomDividerBetweenSections,
  Preset,
  hasSectionImage,
  hasItemImage,
} from 'root/utils/presets';
import { Alignment } from 'root/consts';
import { getItemNameAndIconOrder } from 'root/utils/placement';

export type StylesParamsType<T extends Record<string, StyleParamType>> = ReturnType<typeof createStylesParams<T>>;

export type IStylesParams = IDesignStylesParams & IDisplayStylesParams & ILayoutStylesParams;

export const stylesParams: StylesParamsType<IStylesParams> = {
  ...DisplayStylesParams,
  ...DesignStylesParams,
  ...LayoutStylesParams,
};

export default stylesParams;

export type StyleParamKey = keyof typeof stylesParams;
export type StyleParamValue = IStyleParamValuesByType[keyof IStyleParamValuesByType];

export type StyleParamsByType<T extends StyleParamType> = {
  [K in keyof IStylesParams as IStylesParams[K] extends T ? K : never]: IStylesParams[K];
};

export type StyleParamKeysByType<T extends StyleParamType> = keyof StyleParamsByType<T>;

export type ExtractStyleParamValue<T> = T extends IStyleParam<StyleParamType, infer U> ? U : never;

export const customCssVars: CustomCssVarsFn = ({ isMobile, isRTL, styleParams, tpaData }) => {
  const preset = styleParams.numbers.preset as Preset;
  const columnsItemAlignment = styleParams.numbers.columnsItemAlignment as Alignment;
  const showItemImageParam = styleParams.booleans.showItemImage as boolean;

  const columnsImageWidth = getColumnsImageWidth({
    imageWidthEnum: styleParams.numbers.columnsItemImageSize,
    isMobile,
  });
  const expandStripImageWidth = getColumnsImageWidth({
    imageWidthEnum: styleParams.numbers.expandStripItemImageSize,
    isMobile,
  });
  const menuTextAlignment = getMenuTextAlignment({
    gridMenuAlignment: styleParams.numbers.gridMenuAlignment,
    columnsMenuAlignment: styleParams.numbers.columnsMenuAlignment,
    sideBySideMenuAlignment: styleParams.numbers.sideBySideMenuAlignment,
    expandStripMenuAlignment: styleParams.numbers.expandStripMenuAlignment,
    preset,
  });

  const sectionTextAlignment = getSectionTextAlignment({
    gridSectionAlignment: styleParams.numbers.gridSectionAlignment,
    columnsSectionAlignment: styleParams.numbers.columnsSectionAlignment,
    sideBySideSectionAlignment: styleParams.numbers.sideBySideSectionAlignment,
    expandStripSectionAlignment: styleParams.numbers.expandStripSectionAlignment,
    preset,
  });
  const sectionImageHeight = getSectionImageHeight({
    preset,
    gridSectionImageHeight: styleParams.numbers.gridSectionImageHeight,
    centerSectionImageHeight: styleParams.numbers.centerSectionImageHeight,
    expandStripSectionImageHeight: styleParams.numbers.expandStripSectionImageHeight,
    expandCenterSectionImageHeight: styleParams.numbers.expandCenterSectionImageHeight,
    columnsSectionImageHeight: styleParams.numbers.columnsSectionImageHeight,
  });

  const sectionImageMarginBottom = getSectionImageMarginBottom({ isMobile });

  const sectionGridTemplate = getSectionGridTemplate({ preset });

  const titleContainerDisplay = getTitleContainerDisplay({ preset });
  const titleContainerJustifyContent = getTitleContainerJustifyContent({ preset, sectionTextAlignment });

  const titleContainerFlexDirection = getTitleContainerFlexDirection({
    preset,
    expandStripSectionAlignment: styleParams.numbers.expandStripSectionAlignment,
  });
  const titleContainerPointerEvents = getTitleContainerPointerEvents({ preset });
  const sectionTitleFlexBasis = getSectionTitleFlexBasis({
    preset,
    sectionTextAlignment,
  });

  const itemTextAlignment = getItemTextAlignment({
    gridItemAlignment: styleParams.numbers.gridItemAlignment,
    columnsItemAlignment,
    preset,
  });

  const nameAndPriceJustifyContent = convertAlignmentToJustifyContent({
    alignment: itemTextAlignment,
  });

  const navigationTextAlignment = getHeaderAlignItems({
    preset,
    isMobile,
    gridHeaderAlignItems: styleParams.numbers.gridMenuNavigationAlignment,
    columnsHeaderAlignItems: styleParams.numbers.columnsMenuNavigationAlignment,
    sideBySideHeaderAlignItems: styleParams.numbers.sideBySideMenuNavigationAlignment,
    centerHeaderAlignItems:
      preset === Preset.Center ? Alignment.Center : styleParams.numbers.centerMenuNavigationAlignment,
    expandStripHeaderAlignItems: styleParams.numbers.expandStripMenuNavigationAlignment,
    expandCenterHeaderAlignItems: styleParams.numbers.expandCenterMenuNavigationAlignment,
  });

  const unifiedNavigationTextAlignment = getUnifiedNavigationTextAlignment({
    preset,
    isMobile,
    gridUnifiedNavigationAlignment: styleParams.numbers.gridUnifiedNavigationAlignment,
    sideBySideUnifiedNavigationAlignment: styleParams.numbers.sideBySideUnifiedNavigationAlignment,
    centerUnifiedNavigationAlignment: styleParams.numbers.centerUnifiedNavigationAlignment,
  });
  const locationsContainerAlignment = styleParams.booleans.showSectionTabs ? unifiedNavigationTextAlignment : 'auto';
  const unifiedNavigationGridTemplate = getUnifiedNavigationGridTemplate({
    isMobile,
  });
  const sectionTabsGridTemplateColumns = getSectionTabsGridTemplateColumns({
    isMobile,
    preset,
    gridStretchFullWidth: styleParams.booleans.gridUnifiedNavigationFullWidthTab,
    centerStretchFullWidth: styleParams.booleans.centerUnifiedNavigationFullWidthTab,
    sideBySideStretchFullWidth: styleParams.booleans.sideBySideUnifiedNavigationFullWidthTab,
  });
  const unifiedNavigationGridTemplateColumns = getUnifiedNavigationGridTemplateColumns({
    isMobile,
    preset,
    gridStretchFullWidth: styleParams.booleans.gridUnifiedNavigationFullWidthTab,
    centerStretchFullWidth: styleParams.booleans.centerUnifiedNavigationFullWidthTab,
    sideBySideStretchFullWidth: styleParams.booleans.sideBySideUnifiedNavigationFullWidthTab,
  });
  const showVariantsDivider = showPriceVariantsDivider({
    preset,
    showItemVariantsDivider: styleParams.booleans.showItemVariantsDivider,
  });

  const showMenusSideImage = showMenuSideImage({
    preset,
    isMobile,
    showMenusSideImage: styleParams.booleans.showMenusSideImage,
  });

  const widgetGridTemplate = getWidgetGridTemplate({
    preset,
    isMobile,
    isRTL,
    imagePosition: styleParams.numbers.sideBySideImagePosition,
    hasSectionNavigation: styleParams.booleans.showSectionTabs,
  });

  const widgetPadding = getWidgetPadding({
    preset,
    gridMenuVerticalPadding: styleParams.numbers.gridMenuVerticalPadding,
    gridMenuHorizontalPadding: styleParams.numbers.gridMenuHorizontalPadding,
    columnsMenuVerticalPadding: styleParams.numbers.columnsMenuVerticalPadding,
    columnsMenuHorizontalPadding: styleParams.numbers.columnsMenuHorizontalPadding,
    centerMenuVerticalPadding: styleParams.numbers.centerMenuVerticalPadding,
    centerMenuHorizontalPadding: styleParams.numbers.centerMenuHorizontalPadding,
    sideBySideMenuVerticalPadding: styleParams.numbers.sideBySideMenuVerticalPadding,
    sideBySideMenuHorizontalPadding: styleParams.numbers.sideBySideMenuHorizontalPadding,
    expandStripMenuVerticalPadding: styleParams.numbers.expandStripMenuVerticalPadding,
    expandStripMenuHorizontalPadding: styleParams.numbers.expandStripMenuHorizontalPadding,
    expandCenterMenuVerticalPadding: styleParams.numbers.expandCenterMenuVerticalPadding,
    expandCenterMenuHorizontalPadding: styleParams.numbers.expandCenterMenuHorizontalPadding,
  });

  const widgetOverflow = getWidgetOverflow({
    preset,
    isMobile,
    hasSectionNavigation: styleParams.booleans.showSectionTabs,
  });
  const widgetDisplay = getWidgetDisplay({ preset });
  const headerContainerPadding = getHeaderContainerPadding({ preset, isMobile });
  const locationsPadding = getLocationsWithSectionNavigationPadding({
    preset,
    isMobile,
    hasSectionNavigation: styleParams.booleans.showSectionTabs,
  });

  const headerContainerSectionNavigationPadding = getUnifiedNavigationBackgroundPadding({
    preset,
    gridPadding: styleParams.numbers.gridUnifiedNavigationBackgroundPadding,
    centerPadding: styleParams.numbers.centerUnifiedNavigationBackgroundPadding,
    sideBySidePadding: styleParams.numbers.sideBySideUnifiedNavigationBackgroundPadding,
    isMobile,
  });

  const menuContentGridArea = getMenuContentGridArea({ preset });
  const menuContentPadding = getMenuContentPadding({ preset, isMobile });
  const imageContainerGridArea = getImageContainerGridArea({ preset });
  const imageContainerPosition = getImageContainerPosition({ preset, isMobile });
  const imageContainerTop = getImageContainerTop({ preset });
  const imageContainerWidth = getImageContainerWidth({ preset });
  const imageContainerHeight = getImageContainerHeight({ preset, isMobile });
  const imageContainerPaddingBottom = getImageContainerPaddingBottom({ preset, isMobile });

  const navigationMargin = getNavigationMargin();
  const menuMaxWidth = getMenuMaxWidth({ preset });
  const menuMargin = getMenuMargin({ preset });
  const sectionsDisplay = getSectionsDisplay({ preset });
  const sectionsGridTemplateColumns = getSectionsGridTemplateColumns({ preset });
  const sectionsRowGap = getSectionsRowGap({
    preset,
    gridGap: styleParams.numbers.gridSectionsGap,
    centerGap: styleParams.booleans.showSectionDividers
      ? styleParams.numbers.centerSectionsGap / 2
      : styleParams.numbers.centerSectionsGap,
    expandCenterGap: styleParams.booleans.showSectionDividers
      ? styleParams.numbers.expandCenterSectionsGap / 2
      : styleParams.numbers.expandCenterSectionsGap,
  });
  const sectionsMarginTop = getSectionsMarginTop({
    preset,
    showMenuName: styleParams.booleans.showMenuName,
    showMenuDescription: styleParams.booleans.showMenuDescription,
  });
  const sectionsColumns = getSectionsColumns({
    preset,
    isMobile,
    columnsNumOfColumns: styleParams.numbers.columnsNumOfColumns,
  });
  const sectionsColumnsGap = getSectionsColumnsGap({
    preset,
    columnsGap: styleParams.numbers.columnsColumnsGap,
  });
  const sectionPadding = getSectionPadding({
    preset,
    gridPadding: styleParams.numbers.gridSectionPadding,
    columnsPadding: styleParams.numbers.columnsSectionPadding,
    centerPadding: styleParams.numbers.centerSectionPadding,
    sideBySidePadding: styleParams.numbers.sideBySideSectionPadding,
    expandStripPadding: [
      styleParams.numbers.expandStripSectionPaddingVertical,
      styleParams.numbers.expandStripSectionPaddingHorizontal,
    ],
    expandCenterPadding: [
      styleParams.numbers.expandCenterSectionPaddingVertical,
      styleParams.numbers.expandCenterSectionPaddingHorizontal,
    ],
  });
  const sectionDescriptionBottomPaddingExpand =
    preset === Preset.ExpandCenter ? `${styleParams.numbers.expandCenterSectionPaddingVertical}px` : 0;

  const sectionImagePaddingBottomExpand = getSectionImagePaddingBottomExpand({ preset });

  const sectionMarginBottom = getSectionMarginBottom({
    preset,
    columnsMarginBottom: styleParams.numbers.columnsSectionsGap,
    sideBySideMarginBottom: styleParams.numbers.sideBySideSectionsGap,
    expandStripMarginBottom: styleParams.booleans.showSectionDividers ? 0 : styleParams.numbers.expandStripSectionsGap,
  });
  const bottomDividerBetweenSectionsMargin = getBottomDividerBetweenSectionsMargin({
    preset,
    expandStripBottomDividerMargin: styleParams.booleans.showSectionDividers
      ? styleParams.numbers.expandStripSectionsGap / 2
      : 0,
  });

  const firstSectionMarginTop = getFirstSectionMarginTop({ preset });
  const sectionTopDividerMarginBottom = getSectionTopDividerMarginBottom({
    preset,
    sectionTopDividerWidth: styleParams.numbers.sectionTopDividerWidth,
  });
  const sectionBottomDividerMarginTop = getSectionBottomDividerMarginTop({
    sectionBottomDividerWidth: styleParams.numbers.sectionBottomDividerWidth,
  });
  const itemsDisplay = getItemsDisplay({ preset });
  const showSectionItemsCollapse = [Preset.ExpandCenter, Preset.ExpandStrip].includes(preset) ? 'none' : itemsDisplay;

  const itemsGridTemplateColumns = getItemsGridTemplateColumns({
    preset,
    isMobile,
    gridNumOfColumns: styleParams.numbers.gridNumOfColumns,
  });

  const itemsColumnGap = getItemsColumnGap({ preset, gridItemsColumnGap: styleParams.numbers.gridItemsColumnGap });
  const itemsRowGap = getItemsRowGap({
    preset,
    gridItemsRowGap: styleParams.numbers.gridItemsRowGap,
    centerItemsRowGap: styleParams.numbers.centerItemsRowGap,
    expandCenterItemsRowGap: styleParams.numbers.expandCenterItemsRowGap,
  });

  const itemRootMargin = getItemRootMargin({
    preset,
    columnsItemsGap: styleParams.numbers.columnsItemsGap,
    sideBySideItemsGap: styleParams.numbers.sideBySideItemsGap,
    expandStripItemsGap: styleParams.numbers.expandStripItemsGap,
  });
  const firstItemDividerMarginBottom = getFirstItemDividerMarginBottom({
    itemDividerWidth: styleParams.numbers.itemDividerWidth,
    showItemDividers: styleParams.booleans.showItemDividers,
    itemRootMargin,
  });
  const itemGridTemplate = getItemGridTemplate({
    preset,
    isRTL,
    gridItemDetailsPadding: styleParams.numbers.gridItemDetailsPadding,
    columnsImageWidth,
    expandStripImageWidth,
    centerItemDetailsPadding: styleParams.numbers.centerItemDetailsPadding,
    columnsImagePosition: styleParams.numbers.columnsImagePosition,
    expandStripImagePosition: styleParams.numbers.expandStripImagePosition,
    showItemImage: shouldShowItemImage(showItemImageParam, preset, columnsItemAlignment),
    showDots: styleParams.booleans.showDots,
    isMobile,
    columnsItemAlignment,
  });
  const itemGridTemplateNoImage = getItemGridTemplateNoImage({
    preset,
    isRTL,
    gridItemDetailsPadding: styleParams.numbers.gridItemDetailsPadding,
    columnsImageWidth,
    expandStripImageWidth,
    centerItemDetailsPadding: styleParams.numbers.centerItemDetailsPadding,
    columnsImagePosition: styleParams.numbers.columnsImagePosition,
    expandStripImagePosition: styleParams.numbers.expandStripImagePosition,
    showDots: styleParams.booleans.showDots,
    isMobile,
    columnsItemAlignment,
  });
  const itemPadding = getItemPadding({
    preset,
    gridItemPadding: styleParams.numbers.gridItemPadding,
    columnsItemPadding: styleParams.numbers.columnsItemPadding,
    sideBySideItemPadding: styleParams.numbers.sideBySideItemPadding,
    centerItemPadding: styleParams.numbers.centerItemPadding,
    expandStripItemPadding: styleParams.numbers.expandStripItemPadding,
    expandCenterItemPadding: styleParams.numbers.expandCenterItemPadding,
  });
  const featuredItemPadding = getItemPadding({
    preset,
    gridItemPadding: styleParams.numbers.gridFeaturedItemPadding,
    columnsItemPadding: styleParams.numbers.columnsFeaturedItemPadding,
    sideBySideItemPadding: styleParams.numbers.sideBySideFeaturedItemPadding,
    centerItemPadding: styleParams.numbers.centerFeaturedItemPadding,
    expandStripItemPadding: styleParams.numbers.expandStripFeaturedItemPadding,
    expandCenterItemPadding: styleParams.numbers.expandCenterFeaturedItemPadding,
  });

  const itemPriceJustifySelf = getItemPriceJustifySelf({ preset });
  const itemPriceWidth = getItemPriceWidth({ preset });
  const itemPriceMarginBottom = getItemPriceMarginBottom({ preset });
  const itemImageAspectRatio = getItemImageAspectRatio({
    preset,
    gridItemImageRatio: styleParams.numbers.gridItemImageRatio,
    columnsItemImageRatio: styleParams.numbers.columnsItemImageRatio,
    expandStripItemImageRatio: styleParams.numbers.expandStripItemImageRatio,
  });
  const itemImageMarginBottom = getItemImageMarginBottom({
    preset,
    gridItemImageBottomPadding: styleParams.numbers.gridItemImageBottomPadding,
  });

  const extrasDisplay = getExtrasDisplay({ preset, columnsItemAlignment });
  const showModifierGroupName = getElementDisplay({
    shouldShow: styleParams.booleans.showModifierGroupName,
    displayOption: 'grid',
  });
  const showButtonModifiers = getElementDisplay({
    shouldShow: styleParams.booleans.showModifierGroupName || styleParams.booleans.showModifierName,
    displayOption: 'inline-flex',
  });
  const extrasJustifyContent = getExtrasJustifyContent({ preset, columnsItemAlignment });
  const extrasAlignItems = getExtrasAlignItems({ preset, columnsItemAlignment });
  const extrasFlexWrap = getExtrasFlexWrap({ preset, columnsItemAlignment });
  const modifierGap = getExtrasGap({ preset, gap: '4px', columnsItemAlignment });
  const priceVariantsGap = getExtrasGap({ preset, columnsItemAlignment });

  const variantDisplay = getVariantDisplay({ preset, columnsItemAlignment });
  const variantGridTemplate = getVariantGridTemplate({ preset, columnsItemAlignment });
  const variantAlignItems = getVariantAlignItems({ preset, columnsItemAlignment });

  const variantWidth = getVariantWidth({
    preset,
    showDots: styleParams.booleans.showDots,
    isMobile,
    columnsItemAlignment,
  });
  const variantDetailsJustifyContent = getVariantDetailsJustifyContent({ preset });
  const variantDetailsGridArea = getVariantDetailsGridArea({ preset, columnsItemAlignment });
  const modifierContainerGridTemplate = getModifierContainerGridTemplate({ preset, columnsItemAlignment });
  const showItemDescription = getElementDisplay({ shouldShow: styleParams.booleans.showItemDescription });

  const showItemPrice = getElementDisplay({ shouldShow: styleParams.booleans.showItemPrice, displayOption: 'flex' });

  const showItemCurrency = getElementDisplay({ shouldShow: styleParams.booleans.showItemCurrency });

  const showItemLabels = getElementDisplay({
    shouldShow:
      styleParams.booleans.showItemLabels &&
      (styleParams.booleans.showItemLabelsIcon || styleParams.booleans.showItemLabelsName),
    displayOption: 'flex',
  });

  const showItemLabelsIcon = getElementDisplay({ shouldShow: styleParams.booleans.showItemLabelsIcon });

  const showItemLabelsName = getElementDisplay({ shouldShow: styleParams.booleans.showItemLabelsName });

  const showItemImage = getElementDisplay({
    shouldShow: shouldShowItemImage(showItemImageParam, preset, columnsItemAlignment) && hasItemImage(preset),
  });

  const showItemDividers = getElementDisplay({
    shouldShow: styleParams.booleans.showItemDividers && hasItemDivider(preset),
  });

  const showItemDividerContainer = getElementDisplay({
    shouldShow: hasItemDivider(preset),
    displayOption: 'flex',
  });

  const showItemVariantsPrice = getElementDisplay({ shouldShow: styleParams.booleans.showItemVariantsPrice });
  const showModifierPrice = getElementDisplay({ shouldShow: styleParams.booleans.showModifierPrice });
  const showItemVariantsName = getElementDisplay({ shouldShow: styleParams.booleans.showItemVariantsName });
  const showModifierName = getElementDisplay({
    shouldShow: styleParams.booleans.showModifierName,
    displayOption: 'grid',
  });
  const showMenuNavigation = getElementDisplay({ shouldShow: styleParams.booleans.showTabs });
  const showSectionTabs = getElementDisplay({ shouldShow: styleParams.booleans.showSectionTabs });
  const showNavigationDivider = getElementDisplay({
    shouldShow: !isMobile && styleParams.booleans.showTabs && styleParams.booleans.showSectionTabs,
  });
  const showMenuName = getElementDisplay({ shouldShow: styleParams.booleans.showMenuName });

  const showMenuDescription = getElementDisplay({ shouldShow: styleParams.booleans.showMenuDescription });

  const showSectionName = getElementDisplay({ shouldShow: styleParams.booleans.showSectionName });

  const showSectionDescription = getElementDisplay({ shouldShow: styleParams.booleans.showSectionDescription });
  const showSectionDescriptionCollapse = [Preset.ExpandCenter, Preset.ExpandStrip].includes(preset)
    ? 'none'
    : showSectionDescription;

  const showSectionImage = getElementDisplay({
    shouldShow: styleParams.booleans.showSectionImage && hasSectionImage(preset),
  });

  const showSectionImageCollapse = getShowSectionImageCollapse({ preset, showSectionImage });

  const showTopDividerBetweenSections = getElementDisplay({
    shouldShow: styleParams.booleans.showSectionDividers && hasTopDividerBetweenSections(preset),
  });

  const showBottomDividerBetweenSections = getElementDisplay({
    shouldShow: styleParams.booleans.showSectionDividers && hasBottomDividerBetweenSections(preset),
  });

  const showSectionTopDivider = getElementDisplay({
    shouldShow: styleParams.booleans.showSectionDividers && hasSectionTopDivider(preset),
  });

  const showSectionBottomDivider = getElementDisplay({
    shouldShow: styleParams.booleans.showSectionDividers && hasSectionBottomDivider(preset),
  });

  const showSectionExpandCollapseIcon = getElementDisplay({
    shouldShow: [Preset.ExpandCenter, Preset.ExpandStrip].includes(preset),
  });

  const showDotsBorder = getElementDisplay({
    shouldShow: shouldShowDotsBorder(
      styleParams.booleans.showDots,
      styleParams.booleans.showItemPrice,
      preset,
      columnsItemAlignment
    ),
  });

  const showVariantsDotsBorder = getElementDisplay({
    shouldShow:
      styleParams.booleans.showDots &&
      styleParams.booleans.showItemVariantsName &&
      styleParams.booleans.showItemVariantsPrice,
  });
  const locationNavigationGridArea = getLocationNavigationGridArea({
    preset,
    hasSectionNavigation: styleParams.booleans.showSectionTabs,
  });

  const headerFlexDirection = getHeaderFlexDirection({
    gridHeaderFlexDirection: styleParams.numbers.gridHeaderFlexDirection,
    columnsHeaderFlexDirection: styleParams.numbers.columnsHeaderFlexDirection,
    sideBySideHeaderFlexDirection: styleParams.numbers.sideBySideHeaderFlexDirection,
    centerHeaderFlexDirection: styleParams.numbers.centerHeaderFlexDirection,
    expandStripHeaderFlexDirection: styleParams.numbers.expandStripHeaderFlexDirection,
    expandCenterHeaderFlexDirection: styleParams.numbers.expandCenterHeaderFlexDirection,
    preset,
    isMobile,
  });

  const headerContainerGridArea = getHeaderContainerGridArea({ preset });

  const dotSvgUrl = getDotSvgUrl({ size: styleParams.numbers.dotsSize });
  const nameAndPriceAlignSelf = getNameAndPriceAlignSelf({ showDots: styleParams.booleans.showDots });

  const itemDotsMargin = getDotsMargin({ nameFont: styleParams.fonts.itemTitleTheme });
  const variantDotsMargin = getDotsMargin({ nameFont: styleParams.fonts.variantTitleTheme });

  const headerJustifyContent = getHeaderJustifyContent({
    gridFlexDirection: styleParams.numbers.gridHeaderFlexDirection,
    columnsFlexDirection: styleParams.numbers.columnsHeaderFlexDirection,
    sideBySideFlexDirection: styleParams.numbers.sideBySideHeaderFlexDirection,
    centerFlexDirection: styleParams.numbers.centerHeaderFlexDirection,
    expandStripFlexDirection: styleParams.numbers.expandStripHeaderFlexDirection,
    expandCenterFlexDirection: styleParams.numbers.expandCenterHeaderFlexDirection,
    preset,
    isMobile,
  });

  const showItemGridDivider = getElementDisplay({
    shouldShow: styleParams.booleans.showItemGridDivider && preset === Preset.Grid,
  });

  const itemGridDividerPosition = getItemGridDividerPosition({
    itemsColumnGap: styleParams.numbers.gridItemsColumnGap,
    itemGridDividerWidth: styleParams.numbers.itemGridDividerWidth,
  });

  const expandCollapseIconSize = `${styleParams.numbers.sectionExpandCollapseIconSize}px`;
  const showMoreHoverItalic = styleParams.booleans.showMoreHoverItalic ? 'italic' : 'normal';
  const showMoreHoverBold = styleParams.booleans.showMoreHoverBold ? 'bold' : 'normal';
  const showMoreHoverUnderline = styleParams.booleans.showMoreHoverUnderline ? 'underline' : 'none';
  const showMoreUnderline = styleParams.fonts.showMoreModifiersTheme?.underline ? 'underline' : 'none';

  const { featuredItemNameOrder, featuredItemIconOrder } = getItemNameAndIconOrder(
    styleParams.numbers.featuredItemIconPlacement
  );

  const featuredItemIconDisplay = styleParams.booleans.showFeaturedItemIcon ? 'flex' : 'none';

  const sectionColumnDividerColor = getSectionColumnDividerColor({
    preset,
    sectionColumnDividerColor: styleParams.colors.sectionColumnDividerColor,
    columnsNumOfColumns: styleParams.numbers.columnsNumOfColumns,
  });

  const sectionColumnDividerWidth = getSectionColumnDividerWidth({
    preset,
    sectionColumnDividerWidth: styleParams.numbers.sectionColumnDividerWidth,
    columnsNumOfColumns: styleParams.numbers.columnsNumOfColumns,
  });

  const sectionColumnDividerStyle = getSectionColumnDividerStyle({
    preset,
    showColumnDividers: styleParams.booleans.showColumnDividers,
    columnsNumOfColumns: styleParams.numbers.columnsNumOfColumns,
  });

  const itemGridDividerWidth = `${styleParams.numbers.itemGridDividerWidth}px`;
  const itemGridDividerColor = styleParams.colors.itemGridDividerColor;

  const sectionTopDividerStyles = getDesignedDividerStyles({
    dividerName: 'sectionTopDivider',
    type: styleParams.numbers.sectionTopDividerStyle,
    color: styleParams.colors.sectionTopDividerColor,
    size: styleParams.numbers.sectionTopDividerWidth,
  });

  const sectionBottomDividerStyles = getDesignedDividerStyles({
    dividerName: 'sectionBottomDivider',
    type: styleParams.numbers.sectionBottomDividerStyle,
    color: styleParams.colors.sectionBottomDividerColor,
    size: styleParams.numbers.sectionBottomDividerWidth,
  });

  const itemDividerStyles = getDesignedDividerStyles({
    dividerName: 'itemDivider',
    type: styleParams.numbers.itemDividerStyle,
    color: styleParams.colors.itemDividerColor,
    size: styleParams.numbers.itemDividerWidth,
  });

  const customCss = {
    sectionTabsGridTemplateColumns,
    locationsContainerAlignment,
    columnsImageWidth,
    expandStripImageWidth,
    menuTextAlignment,
    sectionTextAlignment,
    itemTextAlignment,
    nameAndPriceJustifyContent,
    navigationTextAlignment,
    showVariantsDivider,
    showMenusSideImage,
    widgetPadding,
    widgetOverflow,
    widgetDisplay,
    widgetGridTemplate,
    headerContainerPadding,
    menuContentGridArea,
    menuContentPadding,
    imageContainerGridArea,
    imageContainerPosition,
    imageContainerWidth,
    imageContainerHeight,
    imageContainerPaddingBottom,
    imageContainerTop,
    navigationMargin,
    menuMaxWidth,
    menuMargin,
    sectionsDisplay,
    sectionsGridTemplateColumns,
    sectionsRowGap,
    sectionsMarginTop,
    sectionsColumns,
    sectionsColumnsGap,
    sectionPadding,
    sectionMarginBottom,
    sectionImageMarginBottom,
    firstSectionMarginTop,
    sectionTopDividerMarginBottom,
    sectionBottomDividerMarginTop,
    itemsDisplay,
    itemsGridTemplateColumns,
    itemsColumnGap,
    itemsRowGap,
    itemRootMargin,
    firstItemDividerMarginBottom,
    itemGridTemplate,
    itemGridTemplateNoImage,
    itemPadding,
    featuredItemPadding,
    itemPriceJustifySelf,
    itemPriceWidth,
    itemPriceMarginBottom,
    itemImageAspectRatio,
    itemImageMarginBottom,
    itemGridDividerPosition,
    priceVariantsDisplay: extrasDisplay,
    priceVariantsJustifyContent: extrasJustifyContent,
    priceVariantsAlignItems: extrasAlignItems,
    priceVariantsFlexWrap: extrasFlexWrap,
    priceVariantsGap,
    variantDisplay,
    variantGridTemplate,
    variantAlignItems,
    variantWidth,
    variantDetailsJustifyContent,
    variantDetailsGridArea,
    showItemDescription,
    showItemPrice,
    showItemCurrency,
    showItemLabels,
    showItemImage,
    showItemDividers,
    showItemDividerContainer,
    showItemLabelsIcon,
    showItemLabelsName,
    showItemVariantsPrice,
    showItemVariantsName,
    showMenuNavigation,
    showSectionTabs,
    showMenuName,
    showMenuDescription,
    showSectionImage,
    showSectionName,
    showSectionDescription,
    showSectionTopDivider,
    showSectionBottomDivider,
    showTopDividerBetweenSections,
    showBottomDividerBetweenSections,
    showDotsBorder,
    showVariantsDotsBorder,
    dotSvgUrl,
    itemDotsMargin,
    variantDotsMargin,
    nameAndPriceAlignSelf,
    locationNavigationGridArea,
    headerFlexDirection,
    headerContainerGridArea,
    headerJustifyContent,
    showSectionExpandCollapseIcon,
    showSectionItemsCollapse,
    showSectionDescriptionCollapse,
    titleContainerDisplay,
    titleContainerJustifyContent,
    sectionTitleFlexBasis,
    titleContainerFlexDirection,
    titleContainerPointerEvents,
    expandCollapseIconSize,
    sectionDescriptionBottomPaddingExpand,
    bottomDividerBetweenSectionsMargin,
    showModifierPrice,
    showModifierName,
    modifierGroupsJustifyContent: extrasJustifyContent,
    modifierGroupsAlignItems: extrasAlignItems,
    modifierGroupsFlexWrap: extrasFlexWrap,
    modifierGap,
    showModifierGroupName,
    modifierContainerGridTemplate,
    modifiersDisplay: extrasDisplay,
    sectionImageHeight,
    sectionGridTemplate,
    showSectionImageCollapse,
    sectionImagePaddingBottomExpand,
    showMoreHoverItalic,
    showMoreHoverBold,
    showMoreHoverUnderline,
    showButtonModifiers,
    showMoreUnderline,
    featuredItemNameOrder,
    featuredItemIconOrder,
    featuredItemIconDisplay,
    itemGridDividerWidth,
    itemGridDividerColor,
    showItemGridDivider,
    sectionColumnDividerColor,
    sectionColumnDividerWidth,
    sectionColumnDividerStyle,
    unifiedNavigationTextAlignment,
    unifiedNavigationGridTemplate,
    showNavigationDivider,
    locationsPadding,
    headerContainerSectionNavigationPadding,
    unifiedNavigationGridTemplateColumns,
    ...sectionTopDividerStyles,
    ...sectionBottomDividerStyles,
    ...itemDividerStyles,
  };

  return Object.fromEntries(Object.entries(customCss).filter(([_, value]) => value !== ''));
};
